import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  CreditCard, 
  Receipt, 
  CheckCircle2, 
  AlertCircle,
  ChevronLeft,
  DollarSign,
  Calendar,
  Loader2,
  XCircle,
  RefreshCw,
  Gift
} from "lucide-react";
import { Link } from "react-router-dom";

export default function ProBillingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [updatingCard, setUpdatingCard] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [reactivatingSubscription, setReactivatingSubscription] = useState(false);

  const updated = searchParams.get("updated");
  const cancelled = searchParams.get("cancelled");

  useEffect(() => {
    if (updated === "true") {
      toast.success("Payment method updated successfully!");
      window.history.replaceState({}, "", "/pro/billing");
    }
    if (cancelled === "true") {
      toast.info("Card update cancelled");
      window.history.replaceState({}, "", "/pro/billing");
    }
  }, [updated, cancelled]);

  useEffect(() => {
    fetchBillingHistory();
  }, []);

  const fetchBillingHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/pro/billing/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingData(response.data);
    } catch (error) {
      console.error("Error fetching billing history:", error);
      toast.error("Failed to load billing history");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCard = async () => {
    setUpdatingCard(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/pro/billing/update-card`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start card update");
      setUpdatingCard(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.")) {
      return;
    }
    
    setCancellingSubscription(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/pro/billing/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchBillingHistory(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivatingSubscription(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/pro/billing/reactivate-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchBillingHistory(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate subscription");
    } finally {
      setReactivatingSubscription(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatAmount = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "succeeded":
        return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case "failed":
        return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
      case "active":
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">{status}</Badge>;
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case "subscription":
        return <Calendar className="w-5 h-5 text-purple-600" />;
      default:
        return <Receipt className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] pt-6 pb-12 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#d4a853]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] pt-6 pb-12 px-4 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link 
          to="/pro/dashboard" 
          className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6"
          data-testid="billing-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Dashboard
        </Link>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1e3a5f]">
            Billing & Payments
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your subscription, payment methods, and view transaction history
          </p>
        </div>

        {/* Subscription Status Card */}
        {billingData?.subscription ? (
          <Card className="bg-white border-0 shadow-sm mb-6" data-testid="subscription-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <Calendar className="w-5 h-5" />
                Monthly Subscription
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl font-bold text-[#1e3a5f]">$29.99/month</span>
                    {getStatusBadge(billingData.subscription.status)}
                    {billingData.subscription.cancel_at_period_end && (
                      <Badge className="bg-orange-100 text-orange-700">Cancelling</Badge>
                    )}
                  </div>
                  {billingData.subscription.current_period_end && (
                    <p className="text-sm text-slate-500">
                      {billingData.subscription.cancel_at_period_end ? (
                        <>Access ends: {formatDate(billingData.subscription.current_period_end)}</>
                      ) : (
                        <>Next billing: {formatDate(billingData.subscription.current_period_end)}</>
                      )}
                    </p>
                  )}
                </div>
                <div>
                  {billingData.subscription.cancel_at_period_end ? (
                    <Button
                      variant="outline"
                      onClick={handleReactivateSubscription}
                      disabled={reactivatingSubscription}
                      className="min-w-[140px]"
                      data-testid="reactivate-subscription-btn"
                    >
                      {reactivatingSubscription ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Reactivate
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleCancelSubscription}
                      disabled={cancellingSubscription}
                      className="min-w-[140px] text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="cancel-subscription-btn"
                    >
                      {cancellingSubscription ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
              {billingData.subscription.cancel_at_period_end && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-700">
                    Your subscription is scheduled to cancel. You'll continue to have full access until {formatDate(billingData.subscription.current_period_end)}.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Subscription Pending - First Lead Not Yet Received */
          <Card className="bg-white border-0 shadow-sm mb-6" data-testid="subscription-pending-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100">
                  <Gift className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Subscription: Pending</p>
                  <p className="text-sm text-slate-500">Your $29.99/month subscription will start after you receive your first lead (which is FREE!)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Method Card */}
        <Card className="bg-white border-0 shadow-sm mb-6" data-testid="payment-method-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <CreditCard className="w-5 h-5" />
              Payment Method
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billingData?.card_on_file ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 capitalize">
                      {billingData.card_on_file.brand} •••• {billingData.card_on_file.last4}
                    </p>
                    <p className="text-sm text-slate-500">
                      Expires {billingData.card_on_file.exp_month}/{billingData.card_on_file.exp_year}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleUpdateCard}
                  disabled={updatingCard}
                  data-testid="update-card-btn"
                >
                  {updatingCard ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Update Card"
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center py-6">
                <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">No payment method on file</p>
                <Button onClick={handleUpdateCard} disabled={updatingCard} data-testid="add-card-btn">
                  Add Payment Method
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card className="bg-white border-0 shadow-sm" data-testid="transaction-history-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <Receipt className="w-5 h-5" />
              Transaction History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billingData?.transactions?.length > 0 ? (
              <div className="space-y-4">
                {billingData.transactions.map((transaction) => (
                  <div 
                    key={transaction.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    data-testid={`transaction-${transaction.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        {getTransactionIcon(transaction.type)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{transaction.description}</p>
                        <p className="text-sm text-slate-500">{formatDateTime(transaction.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">{formatAmount(transaction.amount)}</p>
                      {getStatusBadge(transaction.status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No transactions yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing Summary */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="pricing-summary">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-800">BQL Pricing</p>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>• <strong>Registration:</strong> FREE</li>
                <li>• <strong>First Lead:</strong> FREE</li>
                <li>• <strong>After First Lead:</strong> $29.99/month — cancel anytime</li>
              </ul>
              <p className="text-sm text-blue-700 mt-2">
                Questions about billing? Contact us at bqleadz@gmail.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
